import { FlexBox } from '@dixa/lib-shared-habla';
import React, { ChangeEvent } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { messengerSettingsFormFields } from '../../../../../types';
import {
  messengerLanguages,
  MessengerTranslatedLanguage,
} from '../../languages';

import { LanguageSelectorItem } from './components';
import { FirstRow } from './components/languageListHeader/LanguageListHeader';
import style from './style.module.scss';

export interface LanguageSelectorProps {
  selectedLanguage: MessengerTranslatedLanguage;
  onSelectLanguage: (language: MessengerTranslatedLanguage) => void;
  defaultLanguageCode: MessengerTranslatedLanguage;
}

export function LanguageSelector({
  selectedLanguage,
  onSelectLanguage,
  defaultLanguageCode,
}: LanguageSelectorProps): JSX.Element {
  const { append, remove } = useFieldArray({
    name: messengerSettingsFormFields.availableLocales,
    rules: { required: true, minLength: 1 },
  });

  const activeLanguages =
    useWatch({ name: messengerSettingsFormFields.availableLocales }) ?? [];

  function onToggleLanguage(
    langCode: MessengerTranslatedLanguage,
    isChecked: boolean,
  ) {
    const index = activeLanguages.findIndex((l) => l === langCode);

    if (isChecked) {
      if (index === -1) {
        append(langCode);
      }
    } else {
      if (index !== -1) {
        remove(index);
      }
    }
  }

  return (
    <FlexBox flexDirection="column" gap="200">
      <FlexBox flexDirection="column" className={style.languagesContainer}>
        <FirstRow />

        {messengerLanguages.map(({ code, label }) => {
          const isEnabled = activeLanguages.includes(code);
          const thereIsOnlyOneEnabledLang = activeLanguages.length === 1;
          const isSelected = selectedLanguage === code;

          const handleSelected = (_: React.MouseEvent) =>
            onSelectLanguage(code);

          const onSwitchChange = (e: ChangeEvent<HTMLInputElement>) =>
            onToggleLanguage(code, e.currentTarget.checked);

          return (
            <LanguageSelectorItem
              key={`${label}-${code}`}
              defaultLanguageCode={defaultLanguageCode}
              langCode={code}
              langName={label}
              isEnabled={isEnabled}
              thereIsOnlyOneEnabledLang={thereIsOnlyOneEnabledLang}
              isCurrentlySelected={isSelected}
              onSwitchChange={onSwitchChange}
              onClick={handleSelected}
            />
          );
        })}
      </FlexBox>
    </FlexBox>
  );
}
